import { HeaderElement, LayoutType, ViewportSize } from "../types";

// ViewportSize and related types
type ContainerType = "container" | "full-width";

// Extended layout types to include new designs
type ExtendedLayoutType =
	| LayoutType
	| "asymmetric"
	| "multirow"
	| "compact"
	| "overlay";

// HeaderLayout Component as HeaderElement Object
const headerLayoutElement: HeaderElement = {
	id: "header-layout-1",
	type: "HeaderLayout",
	tagName: "div", // This will be the outer container
	data: {
		// Layout configuration
		layout: "standard", // "standard", "centered", "split", "stacked"
		containerType: "container", // "container", "full-width"
		isSticky: false,
		viewportSize: "lg",

		// Header state styles
		headerStyles: {
			backgroundColor: "#ffffff",
			padding: "16px 0",
			borderBottom: "1px solid #e5e7eb",
		},

		// Container configuration
		containerMaxWidth: "1200px",
		containerPadding: "0 16px",

		// Component visibility settings
		shouldComponentShow: true,

		// Component behavior settings
		isSelectable: false, // Layout itself isn't selectable
		isDeletable: false,
		hasHoverActions: false,

		// Event handlers
		onComponentClick: "handleComponentClick",
		onComponentRemove: "handleComponentRemove",
	},
	className: "header-layout",
	style: {
		backgroundColor: "#ffffff",
		padding: "16px 0",
		borderBottom: "1px solid #e5e7eb",
	},
	props: {
		role: "banner",
	},
	children: [
		// Container wrapper (conditional based on containerType)
		{
			id: "header-container",
			type: "HeaderContainer",
			tagName: "div",
			data: {
				containerType: "container",
			},
			className: "header-container",
			children: [
				// Layout wrapper (changes based on layout type)
				{
					id: "header-layout-wrapper",
					type: "LayoutWrapper",
					tagName: "div",
					data: {
						layout: "standard",
					},
					className: "layout-wrapper layout-standard",
					children: [
						// Sample child components (these would be dynamically generated)
						{
							id: "logo-component",
							type: "Logo",
							tagName: "div",
							data: {
								componentType: "logo",
								isVisible: true,
							},
							className: "header-component logo-component",
							children: [],
						},
						{
							id: "nav-component",
							type: "NavItems",
							tagName: "div",
							data: {
								componentType: "navItems",
								isVisible: true,
							},
							className: "header-component nav-component",
							children: [],
						},
						{
							id: "search-component",
							type: "SearchBar",
							tagName: "div",
							data: {
								componentType: "searchBar",
								isVisible: true,
							},
							className: "header-component search-component",
							children: [],
						},
						{
							id: "menu-component",
							type: "IconButton",
							tagName: "div",
							data: {
								componentType: "menu",
								isVisible: true,
							},
							className: "header-component menu-component",
							children: [],
						},
					],
				},
			],
		},
	],

	css: `
        /* Header Layout Base Styles */
        .header-layout {
            width: 100%;
            background-color: #ffffff;
            padding: 16px 0;
            border-bottom: 1px solid #e5e7eb;
        }
        
        .header-layout.sticky {
            position: sticky;
            top: 0;
            z-index: 1000;
        }
        
        /* Container Styles */
        .header-container {
            width: 100%;
        }
        
        .header-container.container-type {
            max-width: 1200px;
            margin: 0 auto;
            padding: 0 16px;
        }
        
        .header-container.full-width-type {
            width: 100%;
            padding: 0;
        }
        
        /* Layout Wrapper Base */
        .layout-wrapper {
            width: 100%;
            display: flex;
            align-items: center;
            flex-wrap: wrap;
            gap: 16px;
        }
        
        /* Standard Layout */
        .layout-standard {
            justify-content: flex-start;
        }
        
        /* Centered Layout */
        .layout-centered {
            justify-content: center;
        }
        
        /* Split Layout */
        .layout-split {
            justify-content: space-between;
        }
        
        .layout-split .split-left {
            display: flex;
            align-items: center;
            gap: 16px;
        }
        
        .layout-split .split-right {
            display: flex;
            align-items: center;
            gap: 16px;
        }
        
        /* Stacked Layout */
        .layout-stacked {
            flex-direction: column;
            align-items: center;
            gap: 16px;
        }
        
        .layout-stacked .stacked-top {
            padding: 8px 0;
            width: 100%;
            display: flex;
            justify-content: center;
        }
        
        .layout-stacked .stacked-bottom {
            display: flex;
            justify-content: center;
            align-items: center;
            flex-wrap: wrap;
            gap: 16px;
            width: 100%;
        }
        
        /* Component Styles */
        .header-component {
            display: flex;
            align-items: center;
        }
        
        .header-component.hidden {
            display: none;
        }
        
        /* Responsive Utilities */
        @media (max-width: 576px) {
            .hide-xs {
                display: none !important;
            }
        }
        
        @media (min-width: 577px) and (max-width: 768px) {
            .hide-sm {
                display: none !important;
            }
        }
        
        @media (min-width: 769px) and (max-width: 992px) {
            .hide-md {
                display: none !important;
            }
        }
        
        @media (min-width: 993px) and (max-width: 1200px) {
            .hide-lg {
                display: none !important;
            }
        }
        
        @media (min-width: 1201px) {
            .hide-xl {
                display: none !important;
            }
        }
        
        /* Show only classes */
        .show-only-xs {
            display: none;
        }
        
        @media (max-width: 576px) {
            .show-only-xs {
                display: flex !important;
            }
        }
        
        .show-only-sm {
            display: none;
        }
        
        @media (min-width: 577px) and (max-width: 768px) {
            .show-only-sm {
                display: flex !important;
            }
        }
        
        .show-only-md {
            display: none;
        }
        
        @media (min-width: 769px) and (max-width: 992px) {
            .show-only-md {
                display: flex !important;
            }
        }
        
        .show-only-lg {
            display: none;
        }
        
        @media (min-width: 993px) and (max-width: 1200px) {
            .show-only-lg {
                display: flex !important;
            }
        }
        
        .show-only-xl {
            display: none;
        }
        
        @media (min-width: 1201px) {
            .show-only-xl {
                display: flex !important;
            }
        }
    `,
};

// Factory function to create header layout elements with different configurations
/**
 * Creates a header layout element with the specified configuration
 *
 * @param config - Configuration object for the header layout
 * @param config.id - Unique identifier for the header layout
 * @param config.layout - Layout type ('standard', 'centered', 'split', 'stacked', 'asymmetric', 'multirow', 'compact', 'overlay')
 * @param config.containerType - Container type ('container' or 'full-width')
 * @param config.isSticky - Whether the header should stick to top when scrolling
 * @param config.headerStyles - Custom CSS styles for the header
 * @param config.containerMaxWidth - Maximum width of the container (default: '1200px')
 * @param config.components - Array of child components to be rendered in the header
 * @param config.viewportSize - Viewport size for responsive behavior (default: 'lg')
 *
 * @returns HeaderElement object representing the configured header layout
 */
const createHeaderLayoutElement = (config: {
	id: string;
	layout: ExtendedLayoutType;
	containerType: ContainerType;
	isSticky?: boolean;
	headerStyles?: React.CSSProperties;
	containerMaxWidth?: string;
	components: HeaderElement[]; // Array of child components
	viewportSize?: ViewportSize;
}): HeaderElement => {
	const headerClasses = ["header-layout"];
	if (config.isSticky) {
		headerClasses.push("sticky");
	}

	const containerClasses = ["header-container"];
	if (config.containerType === "container") {
		containerClasses.push("container-type");
	} else {
		containerClasses.push("full-width-type");
	}

	const layoutClasses = ["layout-wrapper", `layout-${config.layout}`];

	// Filter and organize components based on layout type
	const organizeComponentsForLayout = (
		layout: ExtendedLayoutType,
		components: HeaderElement[]
	): HeaderElement[] => {
		const componentsObject: {
			logo: HeaderElement | undefined;
			searchBar: HeaderElement | undefined;
			nav: HeaderElement | undefined;
			actionsButtons: HeaderElement[] | undefined;
			others: HeaderElement[] | undefined;
		} = {
			logo: undefined,
			searchBar: undefined,
			nav: undefined,
			actionsButtons: [],
			others: [],
		};

		for (const component of components) {
			switch (component.type) {
				case "Logo":
					componentsObject.logo = component;
					break;
				case "SearchBar":
					componentsObject.searchBar = component;
					break;
				case "NavItems":
					componentsObject.nav = component;
					break;
				case "IconButton":
					if (!componentsObject.actionsButtons) {
						componentsObject.actionsButtons = [];
					}
					componentsObject.actionsButtons.push(component);

					break;
				default:
					if (!componentsObject.others) {
						componentsObject.others = [];
					}
					componentsObject.others.push(component);
			}
		}

		switch (layout) {
			case "standard":
				return [
					{
						id: `${config.id}-standard-left`,
						type: "StandardContainer",
						tagName: "div",
						className: "standard-left",
						data: {},
						children: [
							componentsObject.logo,
							componentsObject.searchBar,
							...(componentsObject.actionsButtons || []),
						].filter(Boolean) as HeaderElement[],
					},
					{
						id: `${config.id}-standard-right`,
						type: "StandardContainer",
						tagName: "div",
						className: "standard-right",
						data: {},
						children: componentsObject.nav
							? [componentsObject.nav]
							: [],
					},
				];

			case "split":
				const logo = components.find((c) => c.type === "Logo");
				const searchBar = components.find(
					(c) => c.type === "SearchBar"
				);
				const others = components.filter(
					(c) => c.type !== "Logo" && c.type !== "SearchBar"
				);

				return [
					{
						id: `${config.id}-split-left`,
						type: "SplitContainer",
						tagName: "div",
						className: "split-left",
						data: {},
						children: [logo, searchBar].filter(
							Boolean
						) as HeaderElement[],
					},
					{
						id: `${config.id}-split-right`,
						type: "SplitContainer",
						tagName: "div",
						className: "split-right",
						data: {},
						children: others,
					},
				];

			case "stacked":
				const logoStacked = components.find((c) => c.type === "Logo");
				const othersStacked = components.filter(
					(c) => c.type !== "Logo"
				);

				return [
					{
						id: `${config.id}-stacked-top`,
						type: "StackedContainer",
						tagName: "div",
						className: "stacked-top",
						data: {},
						children: logoStacked ? [logoStacked] : [],
					},
					{
						id: `${config.id}-stacked-bottom`,
						type: "StackedContainer",
						tagName: "div",
						className: "stacked-bottom",
						data: {},
						children: othersStacked,
					},
				];

			case "asymmetric":
				// Asymmetric layout: Logo on left, nav in middle, actions on right with different spacing
				const logoAsym = components.find((c) => c.type === "Logo");
				const navItems = components.filter(
					(c) => c.type === "NavItems"
				);
				const actionItems = components.filter(
					(c) => c.type !== "Logo" && c.type !== "NavItems"
				);

				return [
					{
						id: `${config.id}-asym-left`,
						type: "AsymContainer",
						tagName: "div",
						className: "asym-left",
						data: {},
						children: logoAsym ? [logoAsym] : [],
					},
					{
						id: `${config.id}-asym-center`,
						type: "AsymContainer",
						tagName: "div",
						className: "asym-center",
						data: {},
						children: navItems,
					},
					{
						id: `${config.id}-asym-right`,
						type: "AsymContainer",
						tagName: "div",
						className: "asym-right",
						data: {},
						children: actionItems,
					},
				];

			case "multirow":
				// Multi-row layout: Top row for secondary items, bottom row for main navigation
				const topRowItems = components.filter((c) =>
					["searchBar", "login"].includes(c.type as string)
				);
				const bottomRowItems = components.filter(
					(c) => !["searchBar", "login"].includes(c.type as string)
				);

				return [
					{
						id: `${config.id}-multirow-top`,
						type: "MultiRowContainer",
						tagName: "div",
						className: "multirow-top",
						data: {},
						children: topRowItems,
					},
					{
						id: `${config.id}-multirow-bottom`,
						type: "MultiRowContainer",
						tagName: "div",
						className: "multirow-bottom",
						data: {},
						children: bottomRowItems,
					},
				];

			case "compact":
				// Compact layout: All elements in a tight, space-efficient layout
				const logoCompact = components.find((c) => c.type === "Logo");
				const otherCompactItems = components.filter(
					(c) => c.type !== "Logo"
				);

				return [
					{
						id: `${config.id}-compact-left`,
						type: "CompactContainer",
						tagName: "div",
						className: "compact-left",
						data: {},
						children: logoCompact ? [logoCompact] : [],
					},
					{
						id: `${config.id}-compact-right`,
						type: "CompactContainer",
						tagName: "div",
						className: "compact-right",
						data: {},
						children: otherCompactItems,
					},
				];

			case "overlay":
				// Overlay layout: Header overlays the content below it with transparency
				return components;

			default:
				return components;
		}
	};

	const organizedComponents = organizeComponentsForLayout(
		config.layout,
		config.components
	);

	const element: HeaderElement = {
		id: config.id,
		type: "HeaderLayout",
		tagName: "div",
		data: {
			layout: config.layout,
			containerType: config.containerType,
			isSticky: config.isSticky || false,
			viewportSize: config.viewportSize || "lg",
			headerStyles: config.headerStyles || {},
			containerMaxWidth: config.containerMaxWidth || "1200px",
			isSelectable: false,
			isDeletable: false,
			hasHoverActions: false,
		},
		className: headerClasses.join(" "),
		style: {
			backgroundColor: "#ffffff",
			// padding: "16px 0",
			borderBottom: "1px solid #e5e7eb",
			...(config.isSticky && {
				position: "sticky",
				top: 0,
				zIndex: 1000,
			}),
			...(config.layout === "overlay" && {
				backgroundColor: "rgba(255, 255, 255, 0.9)",
				backdropFilter: "blur(8px)",
				position: "absolute",
				width: "100%",
				zIndex: 100,
				borderBottom: "none",
			}),
			...config.headerStyles,
		},
		children: [
			{
				id: `${config.id}-container`,
				type: "HeaderContainer",
				tagName: "div",
				className: containerClasses.join(" "),
				data: {
					containerType: config.containerType,
				},
				style:
					config.containerType === "container"
						? {
								width: "100%",
								maxWidth: config.containerMaxWidth || "1200px",
								margin: "0 auto",
								// padding: "0 16px",
						  }
						: {
								width: "100%",
						  },
				children: [
					{
						id: `${config.id}-layout-wrapper`,
						type: "LayoutWrapper",
						tagName: "div",
						className: layoutClasses.join(" "),
						data: {
							layout: config.layout,
						},
						children: organizedComponents,
					},
				],
			},
		],
		css: generateHeaderLayoutCSS(config),
	};

	return element;
};

// Helper function to generate custom CSS based on configuration
function generateHeaderLayoutCSS(config: {
	containerMaxWidth?: string;
	headerStyles?: React.CSSProperties;
	layout?: ExtendedLayoutType;
}): string {
	return `
        .header-layout {
            width: 100%;
            background-color: #ffffff;
            padding: 16px 0;
            border-bottom: 1px solid #e5e7eb;
        }
        
        .header-layout.sticky {
            position: sticky;
            top: 0;
            z-index: 1000;
        }
        
        .header-container.container-type {
            max-width: ${config.containerMaxWidth || "1200px"};
            margin: 0 auto;
            padding: 0 16px;
        }
        
        .header-container.full-width-type {
            width: 100%;
            padding: 0;
        }
        
        .layout-wrapper {
            width: 100%;
            display: flex;
            align-items: center;
            flex-wrap: wrap;
            gap: 16px;
        }
        
        /* Standard Layout */
        .layout-standard { justify-content: flex-start; }
        .standard-left {
            width: 100%;
            display: flex;
            align-items: center;
            gap: 16px;
        }
        
        /* Centered Layout */
        .layout-centered { justify-content: center; }
        
        /* Split Layout */
        .layout-split { justify-content: space-between; }
        .split-left, .split-right {
            display: flex;
            align-items: center;
            gap: 16px;
        }
        
        /* Stacked Layout */
        .layout-stacked {
            display: flex;
            flex-direction: column; 
            align-items: center; 
            gap: 16px; 
        }
        .stacked-top {
            padding: 8px 0;
            width: 100%;
            display: flex;
            flex-direction: column;
            justify-content: center;
        }
        .stacked-bottom {
            display: flex;
            justify-content: center;
            align-items: center;
            flex-wrap: wrap;
            gap: 16px;
            width: 100%;
        }
        
        /* Asymmetric Layout */
        .layout-asymmetric {
            display: grid;
            grid-template-columns: 1fr 2fr 1fr;
            align-items: center;
        }
        .asym-left {
            display: flex;
            justify-content: flex-start;
            align-items: center;
        }
        .asym-center {
            display: flex;
            justify-content: center;
            align-items: center;
            gap: 24px;
        }
        .asym-right {
            display: flex;
            justify-content: flex-end;
            align-items: center;
            gap: 16px;
        }
        
        /* Multi-row Layout */
        .layout-multirow {
            flex-direction: column;
            gap: 0;
        }
        .multirow-top {
            width: 100%;
            display: flex;
            justify-content: flex-end;
            padding-bottom: 8px;
            border-bottom: 1px solid rgba(0,0,0,0.1);
            margin-bottom: 8px;
            gap: 16px;
        }
        .multirow-bottom {
            width: 100%;
            display: flex;
            justify-content: space-between;
            align-items: center;
        }
        
        /* Compact Layout */
        .layout-compact {
            justify-content: space-between;
            padding: 8px 0;
        }
        .compact-left {
            display: flex;
            align-items: center;
        }
        .compact-right {
            display: flex;
            align-items: center;
            gap: 8px;
        }
        .layout-compact .header-component {
            margin: 0 4px;
        }
        
        /* Overlay Layout */
        .layout-overlay {
            background-color: rgba(255, 255, 255, 0.9);
            backdrop-filter: blur(8px);
            position: absolute;
            width: 100%;
            z-index: 100;
            border-bottom: none;
            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
        }
        
        .header-component {
            display: flex;
            align-items: center;
        }
        
        .header-component.hidden {
            display: none;
        }
        
        /* Responsive Utilities */
        @media (max-width: 576px) { 
            .hide-xs { display: none !important; }
            .layout-asymmetric {
                grid-template-columns: 1fr 1fr;
            }
            .asym-center {
                display: none;
            }
        }
        @media (min-width: 577px) and (max-width: 768px) { 
            .hide-sm { display: none !important; }
            .layout-asymmetric {
                grid-template-columns: 1fr 1fr 1fr;
                gap: 8px;
            }
        }
        @media (min-width: 769px) and (max-width: 992px) { .hide-md { display: none !important; } }
        @media (min-width: 993px) and (max-width: 1200px) { .hide-lg { display: none !important; } }
        @media (min-width: 1201px) { .hide-xl { display: none !important; } }
        
        /* Show only classes */
        .show-only-xs, .show-only-sm, .show-only-md, .show-only-lg, .show-only-xl {
            display: none;
        }
        @media (max-width: 576px) { .show-only-xs { display: flex !important; } }
        @media (min-width: 577px) and (max-width: 768px) { .show-only-sm { display: flex !important; } }
        @media (min-width: 769px) and (max-width: 992px) { .show-only-md { display: flex !important; } }
        @media (min-width: 993px) and (max-width: 1200px) { .show-only-lg { display: flex !important; } }
        @media (min-width: 1201px) { .show-only-xl { display: flex !important; } }
    `;
}

// Predefined layout configurations
const createStandardHeaderLayout = (id: string, components: HeaderElement[]) =>
	createHeaderLayoutElement({
		id,
		layout: "standard",
		containerType: "container",
		components,
	});

const createCenteredHeaderLayout = (id: string, components: HeaderElement[]) =>
	createHeaderLayoutElement({
		id,
		layout: "centered",
		containerType: "container",
		components,
	});

const createSplitHeaderLayout = (id: string, components: HeaderElement[]) =>
	createHeaderLayoutElement({
		id,
		layout: "split",
		containerType: "container",
		components,
	});

const createStackedHeaderLayout = (id: string, components: HeaderElement[]) =>
	createHeaderLayoutElement({
		id,
		layout: "stacked",
		containerType: "container",
		components,
	});

const createStickyHeaderLayout = (
	id: string,
	layout: ExtendedLayoutType,
	components: HeaderElement[]
) =>
	createHeaderLayoutElement({
		id,
		layout,
		containerType: "container",
		isSticky: true,
		components,
	});

// New layout configurations
const createAsymmetricHeaderLayout = (
	id: string,
	components: HeaderElement[]
) =>
	createHeaderLayoutElement({
		id,
		layout: "asymmetric",
		containerType: "container",
		components,
	});

const createMultiRowHeaderLayout = (id: string, components: HeaderElement[]) =>
	createHeaderLayoutElement({
		id,
		layout: "multirow",
		containerType: "container",
		components,
	});

const createCompactHeaderLayout = (id: string, components: HeaderElement[]) =>
	createHeaderLayoutElement({
		id,
		layout: "compact",
		containerType: "container",
		components,
	});

const createOverlayHeaderLayout = (id: string, components: HeaderElement[]) =>
	createHeaderLayoutElement({
		id,
		layout: "overlay",
		containerType: "full-width",
		components,
		headerStyles: {
			backgroundColor: "rgba(255, 255, 255, 0.9)",
			backdropFilter: "blur(8px)",
			boxShadow:
				"0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
		},
	});

export {
	createAsymmetricHeaderLayout,
	createCenteredHeaderLayout,
	createCompactHeaderLayout,
	createHeaderLayoutElement,
	createMultiRowHeaderLayout,
	createOverlayHeaderLayout,
	createSplitHeaderLayout,
	createStackedHeaderLayout,
	createStandardHeaderLayout,
	createStickyHeaderLayout,
	headerLayoutElement,
};
